import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/**
 * The flows any screen can start: they open over the current page, from the
 * phone's "+" sheet, from the Inicio shortcuts, from a client's profile.
 */
export type QuickAction = 'nueva-cita' | 'nueva-clienta' | 'cobrar' | 'nuevo-gasto';

const ACTION_PARAM = 'accion';
/** Extras an action can carry: a preselected client, a preselected day. */
const EXTRA_PARAMS = ['clienta', 'fecha'] as const;
type ExtraParam = (typeof EXTRA_PARAMS)[number];

const ACTIONS: QuickAction[] = ['nueva-cita', 'nueva-clienta', 'cobrar', 'nuevo-gasto'];

interface QuickActionState {
  quickAction?: boolean;
}

/**
 * Opens and closes a quick action through the URL (`?accion=nueva-cita`).
 *
 * In the URL rather than in a store so the phone's Back button closes the
 * dialog instead of leaving the page, and so a link can open one directly.
 * `QuickActionHost` in the shell reads it and mounts the matching dialog.
 */
export default function useQuickAction() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const raw = params.get(ACTION_PARAM);
  const action = ACTIONS.includes(raw as QuickAction) ? (raw as QuickAction) : null;

  const open = useCallback(
    (next: QuickAction, extras: Partial<Record<ExtraParam, string>> = {}) => {
      setParams(
        (current) => {
          const updated = new URLSearchParams(current);
          updated.set(ACTION_PARAM, next);
          for (const key of EXTRA_PARAMS) {
            const value = extras[key];
            if (value) updated.set(key, value);
            else updated.delete(key);
          }
          return updated;
        },
        { state: { quickAction: true } satisfies QuickActionState }
      );
    },
    [setParams]
  );

  const close = useCallback(() => {
    // Opened from inside the app: step back, so Back after closing does not
    // reopen it. Opened from a link: there is nothing to step back to.
    if ((location.state as QuickActionState | null)?.quickAction) {
      void navigate(-1);
      return;
    }
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        updated.delete(ACTION_PARAM);
        for (const key of EXTRA_PARAMS) updated.delete(key);
        return updated;
      },
      { replace: true }
    );
  }, [location.state, navigate, setParams]);

  const extra = (key: ExtraParam): string | undefined => params.get(key) ?? undefined;

  return { action, extra, open, close };
}
